/**
 * Presentation metadata for financial figures.
 *
 * This catalog names a value and says how the interface may present it. It
 * never calculates the value: formulas, thresholds and financial decisions
 * remain in the application/domain read models that produced the API payload.
 */

export type MetricRole = "global" | "primary" | "context" | "detail" | "exception";
export type MetricUnit = "money" | "percent" | "count" | "duration" | "score" | "status";
export type SignPolicy = "unsigned" | "negative-only" | "signed-result" | "adverse";
export type MetricProvenance = "observed" | "derived" | "modelled";

export type MetricDefinition = {
  readonly label: string;
  readonly unit: MetricUnit;
  readonly signPolicy: SignPolicy;
  readonly provenance: MetricProvenance;
  readonly defaultRole: MetricRole;
  readonly helper?: string;
};

export const METRICS = {
  "account.equity": {
    label: "Общий капитал",
    unit: "money",
    signPolicy: "unsigned",
    provenance: "observed",
    defaultRole: "global",
  },
  "account.committed_margin": {
    label: "Используемая маржа",
    unit: "money",
    signPolicy: "unsigned",
    provenance: "derived",
    defaultRole: "primary",
  },
  "account.active_capital": {
    label: "Активный капитал",
    unit: "money",
    signPolicy: "unsigned",
    provenance: "derived",
    defaultRole: "primary",
  },
  "account.position_margin": {
    label: "Маржа в позициях",
    unit: "money",
    signPolicy: "unsigned",
    provenance: "derived",
    defaultRole: "primary",
  },
  "account.reserved_grid_capital": {
    label: "Резерв сеток",
    unit: "money",
    signPolicy: "unsigned",
    provenance: "derived",
    defaultRole: "primary",
  },
  "account.withheld_profit": {
    // The reinvestment share's other part: in equity, not capital for new
    // cycles until it is withdrawn or added back (owner, 2026-10-17).
    label: "Отложено к выводу",
    unit: "money",
    signPolicy: "unsigned",
    provenance: "derived",
    defaultRole: "context",
  },
  "account.withdrawable": {
    // The way out of the capital (owner, 2026-10-30): pause new cycles, let
    // the open ones finish, take out what they no longer hold.
    label: "Можно вывести",
    unit: "money",
    signPolicy: "unsigned",
    provenance: "derived",
    defaultRole: "context",
  },
  "account.strategy_free_capital": {
    label: "Свободный капитал",
    unit: "money",
    signPolicy: "negative-only",
    provenance: "derived",
    defaultRole: "primary",
  },
  "account.major_reserved": {
    // Named for what holds it rather than for what it does. "Safety buffer"
    // was the first label and it was wrong: this share is not idle, it funds
    // the MAJOR pool's own cycle. What is true of it is that the slot pools
    // cannot reach it, and the pool's name is the shortest way to say so.
    label: "Резерв MAJOR",
    unit: "money",
    signPolicy: "unsigned",
    provenance: "derived",
    defaultRole: "context",
  },
  "account.free_margin_modelled": {
    label: "Расчётная свободная маржа",
    unit: "money",
    signPolicy: "unsigned",
    provenance: "derived",
    defaultRole: "primary",
  },
  "allocation.limit": {
    label: "Капитал для торговли",
    unit: "money",
    signPolicy: "unsigned",
    provenance: "modelled",
    defaultRole: "context",
  },
  "allocation.headroom": {
    label: "Свободно для новых циклов",
    unit: "money",
    signPolicy: "negative-only",
    provenance: "modelled",
    defaultRole: "context",
  },
  "allocation.next_cycle": {
    label: "Аллокация на новый цикл",
    unit: "money",
    signPolicy: "unsigned",
    provenance: "modelled",
    defaultRole: "context",
  },
  "allocation.pool_capital": {
    label: "Капитал пула",
    unit: "money",
    signPolicy: "unsigned",
    provenance: "modelled",
    defaultRole: "detail",
  },
  "allocation.pool_unrealised": {
    label: "Нереализованный PnL пула",
    unit: "money",
    signPolicy: "signed-result",
    provenance: "observed",
    defaultRole: "detail",
  },
  "pnl.realised_net": {
    label: "Реализованный PnL",
    unit: "money",
    signPolicy: "signed-result",
    provenance: "observed",
    defaultRole: "primary",
  },
  "pnl.today_realised_net": {
    label: "Сегодня",
    unit: "money",
    signPolicy: "signed-result",
    provenance: "observed",
    defaultRole: "primary",
  },
  "analytics.owner_return": {
    // Named for what it now measures. It used to be the change in the whole
    // account's value over the period, open positions included, which is a
    // different question from the one an owner asks about money placed.
    label: "Доходность на вложенное",
    unit: "percent",
    signPolicy: "signed-result",
    provenance: "derived",
    defaultRole: "primary",
  },
  "analytics.closed_cycles": {
    label: "Закрытые циклы",
    unit: "count",
    signPolicy: "unsigned",
    provenance: "observed",
    defaultRole: "primary",
  },
  "analytics.fees": {
    label: "Комиссии",
    unit: "money",
    signPolicy: "unsigned",
    provenance: "observed",
    defaultRole: "context",
  },
  "analytics.funding": {
    label: "Фандинг",
    unit: "money",
    signPolicy: "signed-result",
    provenance: "observed",
    defaultRole: "context",
  },
  "pnl.unrealised": {
    label: "Нереализованный PnL",
    unit: "money",
    signPolicy: "signed-result",
    provenance: "observed",
    defaultRole: "primary",
  },
  // The rolling migration runs two strategies against each other, and the four
  // figures below are how they are compared. They are separate entries rather
  // than reuses of the account-wide ones because they are measured over one
  // strategy's cycles: a reader who saw "Нереализованный PnL" twice on one
  // screen with two different values would be right to distrust both.
  // All four are `detail`, and that is a statement about the block rather than
  // about the figures. They are printed once per strategy, so a `primary` here
  // would put the same primary metric on the screen twice — and a hierarchy
  // that names two winners has named none. Rank inside the block is carried by
  // size, which is where a comparison of repeated rows should carry it.
  "migration.notional": {
    label: "Капитал в позициях",
    unit: "money",
    signPolicy: "unsigned",
    provenance: "observed",
    defaultRole: "detail",
  },
  "migration.unrealised": {
    label: "Нереализовано",
    unit: "money",
    signPolicy: "signed-result",
    provenance: "observed",
    defaultRole: "detail",
  },
  "migration.realised_net": {
    label: "Чистый результат за 24 ч",
    unit: "money",
    signPolicy: "signed-result",
    provenance: "observed",
    defaultRole: "detail",
  },
  "migration.net_per_capital_day": {
    // The figure the whole block exists for. A daily total rewards whichever
    // strategy was given more money; this divides by the capital and the time
    // it was committed for, which is the only way two strategies running
    // different amounts for different lengths of time can be compared.
    label: "Доходность в день на капитал",
    // A percentage, not money. Net divided by capital-days has the units of a
    // daily rate, and rendering it as USDT prints "+0.09" beside "+0.33" —
    // two decimals of a fraction, which is where a velocity of 0.4 % a day
    // becomes a flat zero.
    unit: "percent",
    signPolicy: "signed-result",
    provenance: "derived",
    defaultRole: "detail",
    helper:
      "Чистый результат закрытых за 24 часа циклов, делённый на капитал, умноженный на время, которое он был занят.",
  },
} as const satisfies Record<string, MetricDefinition>;

export type MetricId = keyof typeof METRICS;

export function metric(id: MetricId): MetricDefinition {
  return METRICS[id];
}

export function metricTestId(id: MetricId): string {
  return `metric-${id}`;
}
